import { ChangeDetectionStrategy, Component, ElementRef, OnInit, afterRenderEffect, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { PharmacyBasketsService, PharmacyOfferModel, PharmacyTrademarkModel, SearchArea, isLocated, isOrderable, stocksEveryLine } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { CalloutComponent } from '../components/common/callout.component';
import { SearchFieldComponent } from '../components/common/search-field.component';
import { SpinnerComponent } from '../components/common/spinner.component';
import { SEGMENTED_GROUP_CLASS, ToggleDirective } from '../components/common/toggle.directive';
import { LocationMapComponent, LocationMarker, MAP_VIEWS, MapView } from './location-map.component';
import { SearchAreaComponent, createSearchAreaState } from './search-area.component';

/** A pharmacy to order from, with the trademark chosen against each prescribed drug. */
export interface BasketChoice {
  pharmacy: PharmacyOfferModel;
  products: PharmacyTrademarkModel[];
}

interface PharmacyRow {
  pharmacy: PharmacyOfferModel;
  /** Why this one cannot be ordered from, or empty where it can. */
  refusalKey: string;
  expanded: boolean;
  holdsChoice: boolean;
  className: string;
}

const ROW_BASE = 'flex w-full flex-col rounded-lg border transition-colors duration-150 motion-reduce:transition-none';
const ROW_OPEN = `${ROW_BASE} border-neutral-200 bg-neutral-0`;
const ROW_CHOSEN = `${ROW_BASE} border-primary-300 bg-primary-50/50`;
const ROW_REFUSED = `${ROW_BASE} border-neutral-100 bg-neutral-50`;

/**
 * Why this pharmacy cannot be ordered from, or empty where it can. The two refusals are different
 * facts: one is a pharmacy our side has never mapped, the other is a shelf that is short.
 */
function refusalKeyOf(pharmacy: PharmacyOfferModel): string {
  if (!pharmacy.partnerId || !pharmacy.locationId) {
    return 'guaranteeV2.message.pharmacyNotConnected';
  }

  return stocksEveryLine(pharmacy) ? '' : 'guaranteeV2.message.pharmacyMissingDrugs';
}

/** How many pharmacies are laid out at once. A city block's worth is a list; a district is not. */
const PAGE_SIZE = 25;

/** Above this many, finding one by eye stops being quicker than naming it. */
const PHARMACY_FILTERABLE = 6;

/** Where a filter looks for what was typed: what the folded row itself puts on the screen. */
function haystackOf(pharmacy: PharmacyOfferModel): string {
  return `${pharmacy.pharmacyName} ${pharmacy.pharmacyAddress}`.toLowerCase();
}

/** The pharmacy's own pick, where it made one — otherwise whatever it listed first. */
function defaultTrademark(trademarks: PharmacyTrademarkModel[]): PharmacyTrademarkModel | undefined {
  return trademarks.find(trademark => trademark.isRecommended) ?? trademarks[0];
}

function defaultProducts(pharmacy: PharmacyOfferModel): PharmacyTrademarkModel[] {
  return pharmacy.prescriptionItems.map(item => defaultTrademark(item.trademarks)).filter((trademark): trademark is PharmacyTrademarkModel => !!trademark);
}

/**
 * The pharmacies that can fill the order, and which trademark answers each prescribed drug.
 *
 * The list is what is actually on the shelf near the member rather than a directory of partners:
 * a pharmacy is here because it holds something for every line, and what it holds is the choice.
 * Each line comes with the pharmacy's own pick already made, so the common case is one click on a
 * pharmacy — the alternatives are there for when the pick is wrong, not to be worked through.
 *
 * A pharmacy our side has never mapped to a partner is shown and refused: nothing can be ordered
 * from it, and leaving it out would make the short list look like the search had failed.
 */
@Component({
  selector: 'guarantees-basket-picker',
  standalone: true,
  imports: [MatIcon, TranslatePipe, CalloutComponent, LocationMapComponent, SearchAreaComponent, SearchFieldComponent, SpinnerComponent, ToggleDirective],
  templateUrl: './basket-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasketPickerComponent implements OnInit {
  memberId = input.required<string>();
  /** The lines the basket is assembled for: a pharmacy is offered only if it answers all of them. */
  prescriptionIds = input.required<string[]>();
  /** What was taken before, so stepping back and forward again shows the answer rather than blanks. */
  initial = input<BasketChoice | undefined>(undefined);
  /** Where the search was centred before. The step is destroyed on leaving it; the answer is not. */
  initialArea = input<SearchArea | undefined>(undefined);

  changed = output<BasketChoice | undefined>();
  areaChanged = output<SearchArea>();

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly baskets = inject(PharmacyBasketsService);

  /** Bumped to ask again after a read that failed — nothing about the question has changed. */
  private readonly attempt = signal(0);

  protected readonly loading = signal(true);
  protected readonly failed = signal(false);

  /** Set when a basket assembled again no longer holds the pharmacy that had been taken. */
  protected readonly choiceLapsed = signal(false);

  private readonly offered = signal<PharmacyOfferModel[]>([]);

  private readonly pharmacy = signal<PharmacyOfferModel | undefined>(undefined);

  /** Which trademark answers each line, by line. Held apart from the pharmacy: it is per pharmacy. */
  private readonly picks = signal<Record<string, string>>({});

  private readonly expandedId = signal<string | undefined>(undefined);

  protected readonly term = signal('');
  protected readonly openOnly = signal(false);

  protected readonly segmentedGroup = SEGMENTED_GROUP_CLASS;
  protected readonly views = MAP_VIEWS;
  /**
   * The map first: it is the control that names the place, so the counters arrive on the thing the
   * manager was already looking at. The list is a step away, and is where the drugs are chosen.
   */
  protected readonly view = signal<MapView>('map');

  /** Where the basket is assembled from. The route insists on a point, so nothing is asked without one. */
  protected readonly searchArea = createSearchAreaState(area => this.areaChanged.emit(area));

  /** The counter whose card is open over the map — taken to be read, whatever else the click did. */
  private readonly focusedId = signal<string | undefined>(undefined);

  protected readonly focusedPharmacy = computed<PharmacyOfferModel | undefined>(() => {
    const id = this.focusedId();

    return id ? this.matched().find(pharmacy => pharmacy.id === id) : undefined;
  });

  protected readonly focusedRefusal = computed(() => {
    const pharmacy = this.focusedPharmacy();

    return pharmacy ? refusalKeyOf(pharmacy) : '';
  });

  /** How much of the narrowed list is laid out. Reset whenever the narrowing itself changes. */
  private readonly page = signal(1);

  /**
   * No basket has been asked for yet, because nobody has said where to assemble it near.
   *
   * This route takes a point as a requirement, and the point it used to be given was Kyiv whoever
   * the member was — so the question is now asked rather than answered on the manager's behalf.
   */
  protected readonly awaitingArea = computed(() => !this.searchArea.area());

  /** How many counters the narrowing left, which is what the list and the map are both showing. */
  protected readonly matchCount = computed(() => this.matched().length);

  /**
   * Whether the map can stand in for the list.
   *
   * It cannot where the counters have no points behind them — an unmapped pharmacy answers with an
   * address and nothing else, and a map of none of them would strand the list behind a view that
   * shows nothing. Empty, though, it maps fine: the circle alone says where we looked.
   */
  private readonly mappable = computed(() => this.matched().length === 0 || this.markers().length > 0);

  /** The map is up while the place is being named, and after that whenever it is the view chosen. */
  protected readonly showMap = computed(() => this.awaitingArea() || (this.view() === 'map' && this.mappable()));

  /**
   * Once wanted, kept: the map is hidden rather than torn down when the list takes the floor, so
   * flicking between the two costs nothing — and here it is wanted from the first breath, since
   * the area is always asked for on this route.
   */
  protected readonly mapLive = signal(false);

  /**
   * Whether any counter could stand on a map at all, read off the whole basket rather than the
   * narrowed one — so the view switch does not vanish under a filter that momentarily matches none.
   */
  protected readonly locatable = computed(() => this.offered().some(pharmacy => isLocated(pharmacy)));

  /** Worth offering a filter at all: a handful is read, a district is searched. */
  protected readonly filterable = computed(() => this.offered().length > PHARMACY_FILTERABLE);

  /** Offered only where it narrows something: with every pharmacy open it would filter nothing. */
  protected readonly narrowsByHours = computed(() => {
    const offered = this.offered();

    return offered.some(pharmacy => pharmacy.isOpenNow) && offered.some(pharmacy => !pharmacy.isOpenNow);
  });

  private readonly matched = computed<PharmacyOfferModel[]>(() => {
    const words = this.term().trim().toLowerCase().split(/\s+/).filter(Boolean);
    const openOnly = this.openOnly();
    const chosenId = this.pharmacy()?.id;

    return this.offered().filter(pharmacy => {
      // What is already taken is never filtered away: a narrowing that hid it would leave the step
      // looking unanswered while an order stood ready behind it.
      if (pharmacy.id === chosenId) {
        return true;
      }

      if (openOnly && !pharmacy.isOpenNow) {
        return false;
      }

      const haystack = haystackOf(pharmacy);

      return words.every(word => haystack.includes(word));
    });
  });

  protected readonly rows = computed<PharmacyRow[]>(() => {
    const expandedId = this.expandedId();
    const chosenId = this.pharmacy()?.id;

    return this.matched()
      .slice(0, this.page() * PAGE_SIZE)
      .map(pharmacy => {
        const refusalKey = refusalKeyOf(pharmacy);
        const holdsChoice = pharmacy.id === chosenId;

        return {
          pharmacy,
          refusalKey,
          expanded: pharmacy.id === expandedId,
          holdsChoice,
          className: refusalKey ? ROW_REFUSED : holdsChoice ? ROW_CHOSEN : ROW_OPEN,
        };
      });
  });

  /**
   * Nowhere holds the drugs — which is a fact about the shelves, not about what has been typed. Read
   * off the whole basket rather than off the narrowed one: a term matching nothing would otherwise
   * replace the box it was typed into, and there would be no way back to the list.
   */
  protected readonly empty = computed(() => !this.loading() && !this.failed() && this.offered().length === 0);

  /** The narrowing left none — a different thing from no pharmacy stocking what was prescribed. */
  protected readonly noMatches = computed(() => !this.loading() && !this.failed() && this.offered().length > 0 && this.matched().length === 0);

  /** What is left beyond the page, so the button can say how much rather than merely that there is. */
  protected readonly hiddenCount = computed(() => Math.max(0, this.matched().length - this.page() * PAGE_SIZE));

  /** What the list is for: how many could actually be ordered from, not how many were found. */
  protected readonly orderableCount = computed(() => this.matched().filter(pharmacy => !refusalKeyOf(pharmacy)).length);

  constructor() {
    effect(() => {
      if (this.showMap()) {
        this.mapLive.set(true);
      }
    });

    // The unfolded row is brought to the reader, wherever in the list it stands — a counter taken
    // from a pin, or restored with the step, may otherwise sit pages of scroll below the fold.
    afterRenderEffect(() => {
      const id = this.expandedId();

      if (id && !this.showMap()) {
        this.host.nativeElement.querySelector(`[data-pharmacy="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });

    combineLatest([toObservable(this.memberId), toObservable(this.prescriptionIds), toObservable(this.searchArea.area), toObservable(this.attempt)])
      .pipe(
        // The lines are the question, and a step returned to hands back the same set in a new array.
        map(([memberId, prescriptionIds, area, attempt]) => ({ memberId, ids: [...prescriptionIds].sort().join(','), area, attempt })),
        // Nothing is asked before the area is settled: this route takes a point as a requirement,
        // and a basket assembled around a guess is a list of the wrong city's pharmacies.
        filter(question => !!question.area),
        distinctUntilChanged(
          (a, b) =>
            a.memberId === b.memberId &&
            a.ids === b.ids &&
            a.attempt === b.attempt &&
            a.area?.latitude === b.area?.latitude &&
            a.area?.longitude === b.area?.longitude &&
            a.area?.radiusMeters === b.area?.radiusMeters
        ),
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
          this.choiceLapsed.set(false);
          this.offered.set([]);
          this.page.set(1);
          this.focusedId.set(undefined);
        }),
        switchMap(question =>
          this.baskets
            .generate(question.memberId, {
              prescriptionIds: question.ids.split(','),
              latitude: question.area!.latitude,
              longitude: question.area!.longitude,
              maxDistanceInMeters: question.area!.radiusMeters,
            })
            .pipe(
              catchError(() => {
                this.failed.set(true);

                return of<PharmacyOfferModel[]>([]);
              })
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe(offered => {
        this.offered.set(offered);
        this.loading.set(false);

        // A read that failed is not an empty shelf, so the pharmacy already taken stands.
        if (!this.failed()) {
          this.settle(offered);
        }
      });
  }

  /** Read here rather than in the constructor: an input has no value until Angular has bound it. */
  ngOnInit(): void {
    // The map is how a place is named, so it opens there — unless a pharmacy was already taken, in
    // which case the list is what the manager came back for.
    this.view.set(this.initial() ? 'list' : 'map');

    // Only where the map opens with nowhere to stand: an area already named frames itself.
    const area = this.initialArea();
    if (area) {
      this.searchArea.restore(area);
    } else {
      this.searchArea.previewHome();
    }

    const initial = this.initial();
    if (!initial) {
      return;
    }

    this.pharmacy.set(initial.pharmacy);
    this.expandedId.set(initial.pharmacy.id);
    this.picks.set(Object.fromEntries(initial.products.map(product => [product.prescriptionId, product.externalId])));
  }

  /** Unfolds the row — and moves the map card with it, so the two surfaces never read different counters. */
  protected toggle(row: PharmacyRow): void {
    if (row.refusalKey) {
      return;
    }

    this.expandedId.set(row.expanded ? undefined : row.pharmacy.id);
    this.focusedId.set(row.expanded ? undefined : row.pharmacy.id);

    // Unfolding is also the choice: the pharmacy's own picks stand until one of them is replaced.
    if (!row.holdsChoice) {
      this.select(row.pharmacy);
    }
  }

  private select(pharmacy: PharmacyOfferModel): void {
    this.choiceLapsed.set(false);
    this.pharmacy.set(pharmacy);
    this.picks.set(Object.fromEntries(defaultProducts(pharmacy).map(product => [product.prescriptionId, product.externalId])));
    this.emit();
  }

  protected pick(trademark: PharmacyTrademarkModel): void {
    this.picks.update(current => ({ ...current, [trademark.prescriptionId]: trademark.externalId }));
    this.emit();
  }

  protected isPicked(trademark: PharmacyTrademarkModel): boolean {
    return this.picks()[trademark.prescriptionId] === trademark.externalId;
  }

  /** Which pin reads as the answer: the pharmacy the order would be placed with. */
  protected readonly chosenId = computed(() => this.pharmacy()?.id);

  /** The pharmacies as points, so a manager can see which of five is the one by the member's flat. */
  protected readonly markers = computed<LocationMarker[]>(() =>
    this.matched()
      .filter(isLocated)
      .map(pharmacy => ({
        id: pharmacy.id,
        title: `${pharmacy.pharmacyName} — ${pharmacy.pharmacyAddress}`,
        latitude: pharmacy.latitude,
        longitude: pharmacy.longitude,
        logoUrl: pharmacy.logoUrl,
        // Greyed rather than hidden: a counter that cannot be ordered from is still a fact of the
        // street, and its card says which of the two refusals this one is.
        muted: !!refusalKeyOf(pharmacy),
      }))
  );

  /**
   * A pin is the pharmacy itself, so taking one is the same choice the row makes — made where it
   * was made, with a card over the map saying what was chosen; a greyed pin gets the card and its
   * refusal instead. The list is still grown to reach the row, so the card's one step onward lands
   * on the trademarks rather than on a page that stops short of them.
   */
  protected chooseMarker(id: string): void {
    const index = this.matched().findIndex(entry => entry.id === id);

    if (index < 0) {
      return;
    }

    const pharmacy = this.matched()[index];

    this.focusedId.set(id);
    this.page.update(page => Math.max(page, Math.floor(index / PAGE_SIZE) + 1));

    if (refusalKeyOf(pharmacy)) {
      return;
    }

    this.expandedId.set(pharmacy.id);

    if (this.pharmacy()?.id !== pharmacy.id) {
      this.select(pharmacy);
    }
  }

  protected dismissCard(): void {
    this.focusedId.set(undefined);
  }

  /** Every narrowing starts the list again: a page reached under one term means nothing under another. */
  protected type(value: string): void {
    this.term.set(value);
    this.page.set(1);
  }

  protected toggleOpen(): void {
    this.openOnly.update(only => !only);
    this.page.set(1);
  }

  protected clearFilter(): void {
    this.term.set('');
    this.openOnly.set(false);
    this.page.set(1);
  }

  protected showMore(): void {
    this.page.update(page => page + 1);
  }

  protected retry(): void {
    this.attempt.update(count => count + 1);
  }

  /**
   * A pharmacy stays taken only while it is still in the basket. What is on the shelf is the whole
   * reason the list is short, so a basket assembled again is the answer as it stands now.
   */
  private settle(offered: PharmacyOfferModel[]): void {
    const chosen = this.pharmacy();
    if (!chosen) {
      return;
    }

    const pharmacy = offered.find(entry => entry.id === chosen.id);
    if (pharmacy && isOrderable(pharmacy)) {
      this.pharmacy.set(pharmacy);
      this.expandedId.set(pharmacy.id);
      this.emit();

      return;
    }

    this.choiceLapsed.set(true);
    this.pharmacy.set(undefined);
    this.picks.set({});
    this.expandedId.set(undefined);
    this.changed.emit(undefined);
  }

  /**
   * The order as it stands: one trademark per line, read back off the pharmacy so what is sent is
   * the pharmacy's own record of its stock rather than anything reassembled here.
   */
  private emit(): void {
    const pharmacy = this.pharmacy();
    if (!pharmacy) {
      this.changed.emit(undefined);

      return;
    }

    const picks = this.picks();

    const products = pharmacy.prescriptionItems
      .map(item => item.trademarks.find(trademark => trademark.externalId === picks[item.prescriptionId]) ?? defaultTrademark(item.trademarks))
      .filter((trademark): trademark is PharmacyTrademarkModel => !!trademark);

    this.changed.emit(products.length === pharmacy.prescriptionItems.length ? { pharmacy, products } : undefined);
  }
}
